import { useState, useEffect, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import { Tabs, Tab, useTheme, MenuItem, Select, IconButton } from '@mui/material';
import ArrowLeft from '@mui/icons-material/ArrowLeft';
import ArrowRight from '@mui/icons-material/ArrowRight';
import { PageCard } from '../../Compounds/CardWithTitle';
import {
  CustomerRunTotal,
  TicketRunTotal,
  getRunTotalsByCustomerSubject,
  getRunTotalsByTicketSubject,
  fetchRunTotalsByCustomerId,
  fetchRunTotalsByTicketId,
  getRunTotalsByCustomerLoadingSubject,
  getRunTotalsByTicketLoadingSubject
} from '../../../store/RunTotal';
import { BasicDataGrid } from '../Table/BasicDataGrid';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { TabPanel } from '../../Atoms/TabPanel';
import { TextSpan } from '../../Atoms/Text';
import { monthlyRecurringColumns } from './MonthlyRecurringColumnDefs';
import { allTimeOneTimeColumns } from './AllTimeOneTimeColumnDefs';
import { runHistoryColumns } from './RunHistoryColumnDefs';
import { getAllTimeOneTimeData, getMonthlyRecurringData } from '../../../utils/helpers';
import { DEFAULT_INLINE_TAB_SX_STYLES, DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { CustomerAttributionTable } from '../Attribution/CustomerAttributionTable';

interface RunTotalsProps {
  customerId?: string | null;
  ticketId?: string | null;
  suplierId?: string;
}

export const RunTotals = ({ customerId, ticketId, suplierId }: RunTotalsProps) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [runTotals, setRunTotals] = useState<CustomerRunTotal[] | TicketRunTotal[]>([]);
  const { id } = useParams();
  const theme = useTheme();
  const tabs = [{ name: 'Reconcile' }, { name: 'Run History' }];
  const [selectedRun, setSelectedRun] = useState<string | null>(null);
  const [currentRunIndex, setCurrentRunIndex] = useState<number>(0);

  useEffect(() => {
    const loadingSub = customerId
      ? getRunTotalsByCustomerLoadingSubject().subscribe((l) => setLoading(l))
      : getRunTotalsByTicketLoadingSubject().subscribe((l) => setLoading(l));
    const fetchData = async () => {
      if (id) {
        if (customerId) {
          fetchRunTotalsByCustomerId(id, suplierId);
        } else {
          fetchRunTotalsByTicketId(id);
        }
      }
    };
    fetchData();

    const subscription = customerId
      ? getRunTotalsByCustomerSubject().subscribe((data) => {
          setRunTotals(data?.totals?.filter((item) => item.id !== 'TOTAL') || []);
        })
      : getRunTotalsByTicketSubject().subscribe((data) => {
          setRunTotals(data?.totals?.filter((item) => item.id !== 'TOTAL') || []);
        });

    return () => {
      subscription.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, [id, customerId, ticketId, suplierId]);

  useEffect(() => {
    if (runTotals && runTotals.length > 0) {
      if (selectedRun === null) {
        setSelectedRun(runTotals[0].rpm_run_id);
        setCurrentRunIndex(0);
      } else {
        const index = runTotals.findIndex((run) => run.rpm_run_id === selectedRun);
        setCurrentRunIndex(index);
      }
    }
  }, [runTotals, selectedRun]);

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
  };

  const handleRunChange = (value: string) => {
    setSelectedRun(value);
  };

  const handlePreviousRun = () => {
    if (currentRunIndex > 0) setSelectedRun(runTotals[currentRunIndex - 1].rpm_run_id);
  };

  const handleNextRun = () => {
    if (currentRunIndex < runTotals.length - 1) setSelectedRun(runTotals[currentRunIndex + 1].rpm_run_id);
  };

  const runOptions = runTotals.map((run) => (
    <MenuItem
      key={run.rpm_run_id}
      value={run.rpm_run_id}>
      {run?.run?.runMonthAbbreviation} {run?.run?.runYear}
    </MenuItem>
  ));

  const fR = runTotals[runTotals.length - 1];
  const lR = runTotals[0];
  const oneTimeLabel =
    runTotals.length > 0
      ? // eslint-disable-next-line max-len
        `${fR?.run?.runMonthAbbreviation} ${fR?.run?.runYear} to ${lR?.run?.runMonthAbbreviation} ${lR?.run?.runYear} Totals`
      : 'One-Time Totals';

  const reconcileTabContent = () => {
    if (customerId) {
      return (
        <CustomerAttributionTable
          customerId={customerId}
          supplierId={suplierId}
        />
      );
    } else {
      return (
        <div className="w-full flex-row flex">
          <div className="w-1/2">
            {runTotals.length > 0 && (
              <div className="flex items-center">
                <IconButton
                  disabled={currentRunIndex <= 0}
                  onClick={handlePreviousRun}>
                  <ArrowLeft />
                </IconButton>
                <Select
                  value={selectedRun || ''}
                  sx={{ height: '1.4rem' }}
                  onChange={(e) => handleRunChange(e.target.value)}>
                  {runOptions}
                </Select>
                <IconButton
                  disabled={currentRunIndex >= runTotals.length - 1}
                  onClick={handleNextRun}>
                  <ArrowRight />
                </IconButton>
              </div>
            )}
            {runTotals.length === 0 && (
              <div className="flex items-center mb-2 mt-[0.65rem] px-4">
                <TextSpan
                  size="sm14"
                  className="flex">
                  Monthly Totals
                </TextSpan>
              </div>
            )}
            <BasicDataGrid
              rows={getMonthlyRecurringData(runTotals, selectedRun)}
              columns={monthlyRecurringColumns}
              sxStyle={{ ...DEFAULT_MINI_TABLE_SX_STYLES, borderRightWidth: 1, borderBottomRightRadius: 0 }}
              className="flex"
              pinnedColumns={{ left: ['id'], right: [] }}
            />
          </div>
          <div className="w-1/2 pl-3">
            <div className="flex items-center mb-2 mt-[0.65rem] px-4">
              <TextSpan
                size="sm14"
                className="flex">
                {oneTimeLabel}
              </TextSpan>
            </div>
            <BasicDataGrid
              rows={getAllTimeOneTimeData(runTotals)}
              columns={allTimeOneTimeColumns}
              sxStyle={{ ...DEFAULT_MINI_TABLE_SX_STYLES, borderLeftWidth: 1, borderBottomLeftRadius: 0 }}
              className="flex"
              pinnedColumns={{ left: ['id'], right: [] }}
            />
          </div>
        </div>
      );
    }
  };

  return (
    <PageCard
      dataTestId="run-totals"
      title="Commissions">
      <div className="w-full">
        <Tabs
          sx={DEFAULT_INLINE_TAB_SX_STYLES}
          value={selectedTab}
          variant="scrollable"
          scrollButtons="auto"
          onChange={(_e, index) => handleTabChange(index)}
          slotProps={{
            indicator: {
              sx: {
                backgroundColor: theme.palette.primary.main,
                height: selectedTab <= tabs.length - 1 ? 2 : 0,
                borderRadius: 1
              }
            }
          }}>
          {tabs.map((tab, index) => (
            <Tab
              sx={{ textTransform: 'none', fontSize: '.8rem', color: 'grey6' }}
              key={index}
              label={`${tab.name}`}
            />
          ))}
        </Tabs>
        {tabs.map((_tab, index) => {
          return (
            <Fragment key={index}>
              {loading && (
                <div className="flex h-[45rem] m-auto">
                  <ComponentSpinner label="Loading data..." />
                </div>
              )}
              {!loading && (
                <TabPanel
                  key={`${index}`}
                  index={index}
                  value={selectedTab}
                  boxPaddingTop={0}>
                  {index === 0 && reconcileTabContent()}
                  {index === 1 && (
                    <div>
                      <div className="basis-full">
                        <BasicDataGrid
                          sxStyle={{ ...DEFAULT_MINI_TABLE_SX_STYLES, maxHeight: '30rem' }}
                          className="flex"
                          rows={runTotals}
                          columns={runHistoryColumns}
                          pinnedColumns={{ left: ['rpm_run_id'], right: [] }}
                        />
                      </div>
                    </div>
                  )}
                </TabPanel>
              )}
            </Fragment>
          );
        })}
      </div>
    </PageCard>
  );
};
